import { lazy, Suspense } from "react";
import { useEffectOnce } from "react-use";
import { Tooltip } from "react-tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import toastify from "@vendors/toastify/toastify";
import { handleStaleAppVersion, Router } from "@utils";
import { useBroadcastableNotifications } from "@hooks";
import CommandPalette from "@components/command_palette/CommandPalette";
import { KinndomContextProvider } from "@contexts/KinndomContext";
import { SessionContextProvider } from "@contexts/SessionContext";

import PageLoader from "@elements/PageLoader";
import Navbar from "@components/navbar/Navbar";
import Button from "@components/elements/Button";
import MobileNavbar from "@components/navbar/MobileNavbar";
import LoginPage from "@pages/LoginPage";

const loadPageComponent = (componentName) => {
  return lazy(() => {
    return import(`./pages/${componentName}`).catch((error) => {
      console.error(error);
      return import("./pages/ErrorFallbackPage");
    });
  });
};

const defaultQueryFn = async ({ queryKey }) => {
  const path = queryKey[0];
  const params = queryKey[1] || {};
  const url = path && Router.buildUrlFor(`/api${path}`, params);

  if (!url) return [];

  return fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "Cache-Control": "no-cache, no-store",
      "Authorization-Token": window.appContext.authorizationToken,
    },
  }).then((response) => {
    handleStaleAppVersion(response);
    if (!response.ok) throw new Error(`API ${response.status}`);

    return response.json();
  }).then((data) => {
    return data.records || [];
  }).catch((error) => {
    // if (error instanceof TypeError && error.message === "Failed to fetch") return [];

    console.error(`[defaultQueryFn] ${error.message}`, { url });
    window.rollbar?.error(`[defaultQueryFn] ${error.message}`, { url });

    return [];
  });
};

export default function App(props) {
  const PageComponent = loadPageComponent(props.component);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        queryFn: defaultQueryFn,
      },
    },
  });

  /**************************************************************************************************/

  useBroadcastableNotifications();

  useEffectOnce(() => {
    if (process.env.NODE_ENV === "production") {
      window.consolePrintKinnMark();
    }
  });

  useEffectOnce(() => {
    toastify.processQueue();
  });

  /**************************************************************************************************/

  if (!window.appContext.isAuthenticated) {
    return (
      <LoginPage {...props} />
    );
  }

  return (
    <KinndomContextProvider>
      <SessionContextProvider>
        <QueryClientProvider client={queryClient}>
          <Suspense fallback={(<PageLoader />)}>
            <PageComponent {...props} />
          </Suspense>

          <Navbar />
          <MobileNavbar />
          <CommandPalette />

          {window.appContext.currentUserMobileBackButtonEnabled && (
            <Button
              className="visible-when-mobile fixed left-0 top-14 z-100 m-1 touch-manipulation select-none"
              iconName="arrow-left"
              isIconOnly={true}
              onClick={() => window.history.back()}
            />
          )}

          <Tooltip
            id="app-tooltip"
            className="tooltip"
            place="bottom"
            opacity={1}
          />
          <div id="modal-root" />
        </QueryClientProvider>
      </SessionContextProvider>
    </KinndomContextProvider>
  );
}
